import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import api from '../../../../api';
import moment from 'moment';

export default function DailyTimetableView({ 
  tenantId, 
  classId, 
  academicYearId, 
  semesterType,
  termId,
  termName,
  isSemesterBased,
  facultyData = [],
  subjects = [],
  classFacultyAssignments = [],
  token,
  electiveGroups = [],
  onOverrideClick,
  labBatches = []
}) {
  const [dailyData, setDailyData] = useState(null);
  const [loading, setLoading] = useState(true); // Changed: start with true
  const [initialLoad, setInitialLoad] = useState(true); // NEW: track initial load
  const [selectedDate, setSelectedDate] = useState(moment());
  const [viewMode, setViewMode] = useState('card');
  const [showOverride, setShowOverride] = useState(true);
  const [overrideInfo, setOverrideInfo] = useState(null);
  const [overrideData, setOverrideData] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null); 
  const [error, setError] = useState('');
  const [fetchedFaculty, setFetchedFaculty] = useState({});
  const [isFetchingFaculty, setIsFetchingFaculty] = useState({});

  const selectedDateStr = useMemo(() => selectedDate.format('YYYY-MM-DD'), [selectedDate]);
  
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);
  const currentRequestIdRef = useRef(0);

  // Build faculty map
  const facultyMap = useMemo(() => {
    const map = new Map();
    
    (facultyData || []).forEach(f => {
      const id = f?.userId || f?._id || f?.staffId || f?.id;
      if (id) {
        const firstName = f?.firstName || f?.name || (f?.user?.firstName) || '';
        const lastName = f?.lastName || f?.surname || (f?.user?.lastName) || '';
        const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
        if (fullName) map.set(String(id), fullName);
      }
    });
    
    return map;
  }, [facultyData]);

  // Build subject map
  const subjectMap = useMemo(() => {
    const map = new Map();
    (subjects || []).forEach(sub => {
      const id = sub?._id || sub?.id;
      if (id) {
        map.set(String(id), {
          name: sub?.name || sub?.subjectName || 'Unknown',
          code: sub?.code || sub?.subjectCode || ''
        });
      }
    });
    return map;
  }, [subjects]);

  // Build lab batch map
  const batchMap = useMemo(() => {
    const map = new Map();
    (labBatches || []).forEach(batch => {
      const id = batch?._id || batch?.id;
      if (id) {
        map.set(String(id), batch?.batchName || `Batch ${batch?.batchNumber || ''}`);
      }
    });
    return map;
  }, [labBatches]);

  // Helper functions
  const getSubjectDisplayName = useCallback((subjectId) => {
    if (!subjectId) return 'Unknown Subject';
    const sub = subjectMap.get(String(subjectId));
    return sub?.name || 'Unknown Subject';
  }, [subjectMap]);

  const getSubjectCode = useCallback((subjectId) => {
    if (!subjectId) return '';
    const sub = subjectMap.get(String(subjectId));
    return sub?.code || '';
  }, [subjectMap]);

  const getBatchDisplayName = useCallback((batchId) => {
    if (!batchId) return '';
    const batch = batchMap.get(String(batchId));
    return batch || '';
  }, [batchMap]);

  const getElectiveGroupName = useCallback((groupId) => {
    const g = electiveGroups.find(x => String(x._id) === String(groupId));
    return g?.groupName || 'Elective Group';
  }, [electiveGroups]);

  const getElectiveGroupLabel = useCallback((groupId) => {
    const g = electiveGroups.find(x => String(x._id) === String(groupId));
    return g?.label || '';
  }, [electiveGroups]);

  const getGroupDisplayName = useCallback((groupId) => {
    const group = electiveGroups.find(g => String(g._id) === String(groupId));
    if (!group) return 'Group';
    
    const groupType = group.groupType || group.groupCategory || group.category || '';
    const isLanguage = groupType === 'language' || groupType === 'Language';
    
    if (isLanguage) {
      return group.groupName || group.name || 'Language';
    }
    return `Elective: ${group.groupName || group.name || 'Elective'}`;
  }, [electiveGroups]);

  const getSessionType = useCallback((entry) => {
    if (!entry) return 'regular';
    
    // Check for break sessions
    if (entry.type === 'break' || entry.isBreak || entry.sessionType === 'break' || 
        entry.name === 'break' || entry.name === 'Break' || entry.label === 'Break' ||
        (entry.label && entry.label.toLowerCase().includes('break'))) {
      return 'break';
    }
    
    // Check for custom sessions
    if (entry.type === 'custom' || entry.isCustom || entry.sessionType === 'custom') {
      return 'custom';
    }
    
    // Check for lab sessions
    if (entry.type === 'lab' || entry.isLab || entry.sessionType === 'lab' || 
        entry.labBatchId || entry.isLabBatch || entry.batchId || 
        (entry.batchId && entry.subjectId)) {
      return 'lab';
    }
    
    // Check for elective/language sessions
    if (entry.electiveGroupId) {
      const group = electiveGroups.find(g => String(g._id) === String(entry.electiveGroupId));
      const groupType = group?.groupType || group?.groupCategory || group?.category || '';
      if (groupType === 'language' || groupType === 'Language') {
        return 'language';
      }
      return 'elective';
    }
    
    return 'regular';
  }, [electiveGroups]);

  const fetchFacultyName = useCallback(async (facultyId) => {
    if (!facultyId) return '';
    const idStr = String(facultyId);
    
    if (fetchedFaculty[idStr]) return fetchedFaculty[idStr];
    if (isFetchingFaculty[idStr]) return 'Loading...';
    
    setIsFetchingFaculty(prev => ({ ...prev, [idStr]: true }));
    
    try {
      const response = await api.get(`/api/users/${idStr}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });
      
      let facultyName = '';
      if (response.data?.user) {
        const user = response.data.user;
        facultyName = user.name || [user.firstName, user.lastName].filter(Boolean).join(' ') || '';
      } else if (response.data) {
        facultyName = response.data.name || response.data.firstName || '';
      }
      
      const finalName = facultyName || 'Not Found';
      if (isMountedRef.current) {
        setFetchedFaculty(prev => ({ ...prev, [idStr]: finalName }));
      }
      return finalName;
    } catch (err) {
      console.error('Error fetching faculty:', err);
      if (isMountedRef.current) {
        setFetchedFaculty(prev => ({ ...prev, [idStr]: 'Not Found' }));
      }
      return 'Not Found';
    } finally {
      if (isMountedRef.current) {
        setIsFetchingFaculty(prev => ({ ...prev, [idStr]: false }));
      }
    }
  }, [token, fetchedFaculty, isFetchingFaculty]);

  const getFacultyName = useCallback(async (facultyId) => {
    if (!facultyId) return '';
    const idStr = String(facultyId);
    if (facultyMap.get(idStr)) return facultyMap.get(idStr);
    if (fetchedFaculty[idStr]) return fetchedFaculty[idStr];
    return await fetchFacultyName(facultyId);
  }, [facultyMap, fetchedFaculty, fetchFacultyName]);

  const enrichElectiveSelectionsWithSubgroups = useCallback((entry) => {
    if (!entry.electiveSelections) return entry;
    
    return {
      ...entry,
      electiveSelections: entry.electiveSelections.map(sel => ({
        ...sel,
        subgroupSelections: sel.subgroupSelections || []
      }))
    };
  }, []);

  const processEntry = useCallback(async (entry) => {
    if (!entry) return null;

    try {
      const enrichedEntry = enrichElectiveSelectionsWithSubgroups(entry);
      
      // Determine session type FIRST
      const sessionType = getSessionType(enrichedEntry);
      const isLab = sessionType === 'lab';
      const isBreak = sessionType === 'break';
      const isCustom = sessionType === 'custom';
      
      let facultyId = enrichedEntry.facultyUserId || enrichedEntry.facultyId;
      let facultyName = enrichedEntry.facultyName || '';
      let batchName = '';
      let labBatchId = null;

      // Handle lab batches
      if (isLab) {
        labBatchId = enrichedEntry.labBatchId || enrichedEntry.batchId || enrichedEntry.labBatch?._id || enrichedEntry.batch?._id;

        let subjectIdForLab = enrichedEntry.subjectId || enrichedEntry.labSubjectId;

        if (labBatchId && typeof labBatchId === 'object') {
          labBatchId = labBatchId._id || labBatchId.id;
        }

        if (subjectIdForLab && typeof subjectIdForLab === 'object') {
          subjectIdForLab = subjectIdForLab._id || subjectIdForLab.id;
        }

        if (labBatchId) {
          batchName = getBatchDisplayName(labBatchId) || enrichedEntry.batchName || enrichedEntry.labBatchName || '';
        }

        if (!batchName && labBatchId) {
          const foundBatch = labBatches.find(b => String(b._id) === String(labBatchId));
          if (foundBatch) {
            batchName = foundBatch.batchName || `Batch ${foundBatch.batchNumber || ''}`;
          }
        }

        if (labBatchId && subjectIdForLab && !facultyName) {
          try {
            const res = await api.get(`/api/classManager/lab-batches/${labBatchId}/subjects/${subjectIdForLab}/faculty`, {
              headers: token ? { Authorization: `Bearer ${token}` } : undefined
            });
            if (res.data && Array.isArray(res.data.faculty) && res.data.faculty.length > 0) {
              const names = await Promise.all(res.data.faculty.map(async f => {
                if (f.name) return f.name;
                if (f.facultyId) return await getFacultyName(f.facultyId);
                return '';
              }));
              facultyName = names.filter(Boolean).join(', ');
            } else if (res.data && res.data.user) {
              const user = res.data.user;
              facultyId = user._id || user.id;
              facultyName = user.name || [user.firstName, user.lastName].filter(Boolean).join(' ') || '';
            }
          } catch (err) {
            console.error('Error fetching lab batch faculty:', err);
          }
        }
      }
      
      // Handle non-lab batches
      if (!isLab && enrichedEntry.batchId) {
        batchName = getBatchDisplayName(enrichedEntry.batchId) || enrichedEntry.batchName || '';
      }

      // Fetch faculty name if not resolved
      if (facultyId && !facultyName && !isBreak && !isCustom) {
        facultyName = await getFacultyName(facultyId);
      }

      // Process elective selections
      const processedElectives = await Promise.all((enrichedEntry.electiveSelections || []).map(async (sel) => {
        let subjectFacultyName = sel.facultyName || '';
        if (sel.facultyId && !subjectFacultyName) {
          subjectFacultyName = await getFacultyName(sel.facultyId);
        }

        const processedSubgroups = await Promise.all((sel.subgroupSelections || []).map(async (sg) => {
          let sgFacultyName = sg.facultyName || '';
          if (sg.facultyId && !sgFacultyName) {
            sgFacultyName = await getFacultyName(sg.facultyId);
          }
          return {
            ...sg,
            facultyName: sgFacultyName || 'Not Assigned'
          };
        }));
        return {
          ...sel,
          facultyName: subjectFacultyName || 'Not Assigned',
          subgroupSelections: processedSubgroups
        };
      }));

      const isElective = sessionType === 'elective' || sessionType === 'language';
      const isLanguage = sessionType === 'language';

      let breakLabel = '';
      if (isBreak) {
        breakLabel = enrichedEntry.label || enrichedEntry.name || enrichedEntry.title || '';
        if (!breakLabel) {
          if (enrichedEntry.timeSlot?.start) {
            const startHour = parseInt(enrichedEntry.timeSlot.start.split(':')[0]);
            if (startHour < 11) breakLabel = 'Morning Break';
            else if (startHour < 14) breakLabel = 'Lunch Break';
            else breakLabel = 'Evening Break';
          } else {
            breakLabel = 'BREAK';
          }
        }
      }

      // IMPORTANT: Always return the entry, don't filter out
      return {
        ...enrichedEntry,
        facultyName: (isBreak || isCustom) ? '' : (facultyName || 'No Faculty'),
        electiveSelections: processedElectives,
        sessionType,
        isElective,
        isLanguage,
        isLab,
        isBreak,
        isCustom,
        electiveGroupName: enrichedEntry.electiveGroupId ? getElectiveGroupName(enrichedEntry.electiveGroupId) : null,
        electiveGroupLabel: enrichedEntry.electiveGroupId ? getElectiveGroupLabel(enrichedEntry.electiveGroupId) : null,
        electiveGroupDisplay: enrichedEntry.electiveGroupId ? getGroupDisplayName(enrichedEntry.electiveGroupId) : null,
        subjectName: isBreak ? breakLabel : (enrichedEntry.subjectName || getSubjectDisplayName(enrichedEntry.subjectId)),
        subjectCode: isBreak ? '' : (enrichedEntry.subjectCode || getSubjectCode(enrichedEntry.subjectId)),
        batchName: batchName,
        labBatchId: labBatchId,
        label: breakLabel || enrichedEntry.label,
        displayTitle: isBreak ? breakLabel : (isLab && batchName ? `${enrichedEntry.subjectName || enrichedEntry.label || 'Lab'} (${batchName})` : (enrichedEntry.subjectName || enrichedEntry.label || 'Session'))
      };
    } catch (err) {
      console.error('Error processing entry:', err, entry);
      // Return a basic version of the entry to prevent data loss
      return {
        ...entry,
        sessionType: 'regular',
        facultyName: 'Error Loading',
        subjectName: entry.subjectName || entry.label || 'Unknown',
        displayTitle: entry.subjectName || entry.label || 'Unknown'
      };
    }
  }, [enrichElectiveSelectionsWithSubgroups, getFacultyName, getSessionType, getBatchDisplayName, getElectiveGroupName, getElectiveGroupLabel, getGroupDisplayName, getSubjectDisplayName, getSubjectCode, token, labBatches]);

  const fetchAllData = useCallback(async (dateStr) => {
    if (!tenantId || !classId || !academicYearId || !isMountedRef.current) return;
    
    currentRequestIdRef.current += 1;
    const thisRequestId = currentRequestIdRef.current;
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    setLoading(true);
    setError('');
    setInitialLoad(false); // Mark that initial load is done
    
    try {
      // Fetch regular timetable data
      const timetableParams = { 
        tenantId, 
        classId, 
        academicYearId, 
        selectedDate: dateStr 
      };
      
      if (!isSemesterBased && termId) {
        timetableParams.termId = termId;
      } else if (isSemesterBased) {
        if (semesterType) {
          timetableParams.semesterType = semesterType;
        }
      }
      
      console.log('Fetching timetable with params:', timetableParams);
      
      const timetableRes = await api.get('/api/timetable/daily', { 
        params: timetableParams,
        signal: abortController.signal 
      });
      
      // Check if this request is still valid
      if (thisRequestId !== currentRequestIdRef.current || !isMountedRef.current) return;
      
      let entries = [];
      let skip = false;
      
      // FIXED: Better response handling with multiple possible response structures
      if (timetableRes.data) {
        if (timetableRes.data.success === true || timetableRes.data.status === 'success') {
          const data = timetableRes.data.data || timetableRes.data;
          entries = data?.entries || data || [];
          skip = data?.skip === true;
        } else if (Array.isArray(timetableRes.data)) {
          entries = timetableRes.data;
        } else if (timetableRes.data.entries) {
          entries = timetableRes.data.entries;
          skip = timetableRes.data.skip === true;
        } else {
          console.warn('Unexpected API response structure:', timetableRes.data);
          entries = [];
        }
      }
      
      console.log('Raw entries from API:', entries);
      console.log('Skip flag:', skip);
      
      // Process entries safely
      let processedEntries = [];
      if (entries && entries.length > 0) {
        processedEntries = await Promise.all(
          entries.map(entry => processEntry(entry).catch(err => {
            console.error('Error processing single entry:', err, entry);
            return null;
          }))
        );
        processedEntries = processedEntries.filter(entry => entry !== null);
      }
      
      console.log('Processed entries count:', processedEntries.length);
      
      if (isMountedRef.current && thisRequestId === currentRequestIdRef.current) {
        setDailyData({
          entries: processedEntries,
          hasOverride: false,
          skip: skip
        });
      }
      
      // Check for override
      const overrideParams = { 
        tenantId, 
        classId, 
        academicYearId, 
        targetDate: dateStr 
      };
      if (!isSemesterBased && termId) {
        overrideParams.termId = termId;
      } else if (isSemesterBased && semesterType) {
        overrideParams.semesterType = semesterType;
      }
      
      try {
        const overrideCheckRes = await api.get('/api/timetable/daily-override/check-exists', { 
          params: overrideParams,
          signal: abortController.signal 
        });
        
        if (thisRequestId !== currentRequestIdRef.current || !isMountedRef.current) return;
        
        if (overrideCheckRes.data?.success && overrideCheckRes.data?.exists) {
          setOverrideInfo({ hasOverride: true });
          
          const overrideDataRes = await api.get('/api/timetable/daily-override/get', { 
            params: overrideParams,
            signal: abortController.signal 
          });
          
          if (thisRequestId !== currentRequestIdRef.current || !isMountedRef.current) return;
          
          if (overrideDataRes.data?.success && overrideDataRes.data?.data) {
            const override = overrideDataRes.data.data;
            let overrideEntries = override.overriddenEntries || [];
            
            let processedOverrideEntries = [];
            if (overrideEntries.length > 0) {
              processedOverrideEntries = await Promise.all(
                overrideEntries.map(entry => processEntry(entry).catch(err => {
                  console.error('Error processing override entry:', err, entry);
                  return null;
                }))
              );
              processedOverrideEntries = processedOverrideEntries.filter(entry => entry !== null);
            }
            
            if (isMountedRef.current && thisRequestId === currentRequestIdRef.current) {
              setOverrideData({
                entries: processedOverrideEntries,
                description: override.description,
                reason: override.reason
              });
            }
          }
        } else {
          if (isMountedRef.current && thisRequestId === currentRequestIdRef.current) {
            setOverrideInfo({ hasOverride: false });
          }
        }
      } catch (overrideErr) {
        // Don't fail the whole timetable if override check fails
        console.error('Error checking override:', overrideErr);
        if (isMountedRef.current && thisRequestId === currentRequestIdRef.current) {
          setOverrideInfo({ hasOverride: false });
        }
      }
      
    } catch (err) {
      if (err.name !== 'AbortError' && isMountedRef.current && thisRequestId === currentRequestIdRef.current) {
        console.error('Error fetching timetable:', err);
        setError(err.response?.data?.message || 'Failed to load timetable data. Please try again.');
        setDailyData({ entries: [], skip: false, hasOverride: false });
        setOverrideData(null);
        setOverrideInfo(null);
      }
    } finally {
      if (isMountedRef.current && thisRequestId === currentRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [tenantId, classId, academicYearId, semesterType, termId, isSemesterBased, processEntry]);

  const dateOptions = useMemo(() => {
    const options = [];
    const today = moment();
    for (let i = 7; i > 0; i--) {
      const date = today.clone().subtract(i, 'days');
      options.push({ value: date.format('YYYY-MM-DD'), label: date.format('ddd, MMM DD, YYYY') });
    }
    options.push({ value: today.format('YYYY-MM-DD'), label: `Today: ${today.format('ddd, MMM DD, YYYY')}` });
    for (let i = 1; i <= 7; i++) {
      const date = today.clone().add(i, 'days');
      options.push({ value: date.format('YYYY-MM-DD'), label: date.format('ddd, MMM DD, YYYY') });
    }
    return options;
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    setInitialLoad(true);
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (tenantId && classId && academicYearId) {
      fetchAllData(selectedDateStr);
    }
  }, [tenantId, classId, academicYearId, semesterType, termId, selectedDateStr, fetchAllData]);

  const handleDateChange = (date) => {
    setLoading(true);
    setError('');
    setDailyData(null);
    setOverrideData(null);
    setOverrideInfo(null);
    setSelectedDate(moment(date));
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  const navigateDay = (dir) => {
    setLoading(true);
    setError('');
    setDailyData(null);
    setOverrideData(null);
    setOverrideInfo(null);
    setSelectedDate(prev => prev.clone().add(dir, 'days'));
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  const displayData = useMemo(() => {
    const base = dailyData || { entries: [], skip: false, hasOverride: overrideInfo?.hasOverride };
    if (showOverride && overrideInfo?.hasOverride && overrideData) {
      return { ...base, entries: overrideData.entries || [], isOverride: true, overrideInfo: overrideData };
    }
    return { ...base, isOverride: false };
  }, [showOverride, overrideInfo, overrideData, dailyData]);

  const formatTime = (t) => t ? moment(t, 'HH:mm').format('h:mm A') : '';

  // FIXED: Proper loading and empty state handling
  const showLoading = loading || (initialLoad && !dailyData);
  const showEmptyState = !showLoading && !error && (!displayData.entries || displayData.entries.length === 0);
  const showBreakState = !showLoading && !error && displayData.skip === true && (!displayData.entries || displayData.entries.length === 0);
  const showError = !showLoading && error;
  const showContent = !showLoading && !error && displayData.entries && displayData.entries.length > 0;

  return (
    <div className="daily-timetable bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* HEADER SECTION */}
      <div className="bg-white p-6 border-b border-gray-200">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Daily Academic Schedule</h1>
            <div className="flex items-center gap-2">
              <p className="text-gray-600 font-medium">{selectedDate.format('dddd, MMMM DD, YYYY')}</p>
              <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                {isSemesterBased ? `Semester ${semesterType || ''}` : termName || `Term ${termId?.slice(-6) || ''}`}
              </span>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center bg-gray-100 rounded-lg p-1 border border-gray-200">
              <button onClick={() => navigateDay(-1)} className="p-2 hover:bg-white rounded-md transition-all shadow-sm">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" /></svg>
              </button>
              <input 
                type="date" 
                value={selectedDateStr} 
                onChange={(e) => handleDateChange(e.target.value)} 
                className="bg-transparent border-none text-gray-900 font-bold focus:ring-0 px-2 text-sm cursor-pointer"
              />
              <button onClick={() => navigateDay(1)} className="p-2 hover:bg-white rounded-md transition-all shadow-sm">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" /></svg>
              </button>
            </div>

            <select
              value={selectedDateStr}
              onChange={(e) => handleDateChange(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none"
            >
              {dateOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
            
            <div className="flex bg-gray-100 p-1 rounded-lg border">
              <button onClick={() => setViewMode('card')} className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${viewMode === 'card' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500'}`}>CARDS</button>
              <button onClick={() => setViewMode('table')} className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${viewMode === 'table' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500'}`}>LIST</button>
            </div>
          </div>
        </div>
      </div>

      {/* STATUS & OVERRIDE CONTROLS */}
      <div className="px-6 py-3 bg-gray-50 border-b flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {!showLoading && !showError && (
            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border ${
              displayData.skip ? 'bg-red-50 text-red-600 border-red-200' : 
              showBreakState ? 'bg-red-50 text-red-600 border-red-200' :
              'bg-green-50 text-green-600 border-green-200'
            }`}>
              {displayData.skip || showBreakState ? 'Academic Break' : `${displayData.entries?.length || 0} Sessions Scheduled`}
            </span>
          )}
          {overrideInfo?.hasOverride && (
            <button 
              onClick={() => setShowOverride(!showOverride)}
              className={`text-xs font-bold px-3 py-1 rounded-full border transition-all ${showOverride ? 'bg-orange-500 text-white border-orange-600' : 'bg-white text-orange-600 border-orange-200'}`}
            >
              {showOverride ? 'Showing Override' : 'Show Override'}
            </button>
          )}
        </div>
        {displayData.isOverride && (
          <span className="text-xs font-bold text-orange-600 flex items-center gap-1 animate-pulse">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" strokeWidth={2} /></svg>
            OVERRIDE ACTIVE
          </span>
        )}
      </div>

      {/* CONTENT AREA */}
      <div className="p-6 min-h-[300px]">
        {/* Loading State */}
        {showLoading && (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-500 font-medium">Loading schedule...</p>
          </div>
        )}

        {/* Error State */}
        {showError && (
          <div className="text-center py-16 bg-red-50 rounded-xl border border-red-100">
            <h3 className="text-lg font-bold text-red-800 mb-2">Schedule Unavailable</h3>
            <p className="text-red-600 mb-6 text-sm">{error}</p>
            <button onClick={() => handleDateChange(selectedDateStr)} className="px-6 py-2 bg-red-600 text-white rounded-lg font-bold text-sm shadow-lg shadow-red-200">Retry Fetch</button>
          </div>
        )}

        {/* Academic Break State */}
        {showBreakState && (
          <div className="text-center py-20 bg-red-50 rounded-xl border-2 border-dashed border-red-200">
            <svg className="w-16 h-16 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-red-600 font-bold text-lg">Academic Break</p>
            <p className="text-red-400 text-sm mt-1">No classes scheduled for this day</p>
          </div>
        )}

        {/* Empty State */}
        {showEmptyState && !showBreakState && (
          <div className="text-center py-20 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-gray-500 font-bold text-lg">No sessions scheduled for this day</p>
            <p className="text-gray-400 text-sm mt-1">Select another date from the calendar above</p>
          </div>
        )}

        {/* Content State */}
        {showContent && (
          <div className={viewMode === 'card' ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" : "overflow-x-auto border rounded-xl"}>
            {viewMode === 'card' ? (
              displayData.entries.map((entry, idx) => {
                const sessionType = entry.sessionType || getSessionType(entry);
                const isElective = sessionType === 'elective' || sessionType === 'language';
                const isLanguage = sessionType === 'language';
                const isBreak = sessionType === 'break';
                const isCustom = sessionType === 'custom';
                const isLab = sessionType === 'lab';
                
                let borderColorClass = 'border-l-blue-500 border-blue-100';
                let bgClass = 'bg-white';
                if (isBreak) borderColorClass = 'border-l-amber-500 border-amber-100';
                else if (isCustom) borderColorClass = 'border-l-orange-500 border-orange-100';
                else if (isLab) borderColorClass = 'border-l-purple-500 border-purple-100';
                else if (isLanguage) borderColorClass = 'border-l-green-500 border-green-100';
                else if (isElective) borderColorClass = 'border-l-indigo-500 border-indigo-100';
                
                const displayTitle = entry.displayTitle || 
                  (isBreak ? (entry.label || entry.name || 'BREAK') :
                   isCustom ? (entry.label || 'CUSTOM') :
                   isLab && entry.batchName ? `${entry.subjectName || entry.label || 'Lab'} (${entry.batchName})` :
                   isElective ? (entry.electiveGroupDisplay || getGroupDisplayName(entry.electiveGroupId)) :
                   entry.subjectName || entry.label || 'Session');
                
                return (
                  <div 
                    key={idx} 
                    onClick={() => setSelectedEntry(entry)}
                    className={`p-5 rounded-2xl border-l-4 shadow-sm hover:shadow-md transition-all cursor-pointer ${bgClass} border ${borderColorClass}`}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="font-bold text-gray-900 text-lg leading-tight">
                          {displayTitle}
                        </h3>
                        {entry.subjectCode && !isElective && !isBreak && !isCustom && (
                          <p className="text-xs font-bold text-gray-400 mt-1">{entry.subjectCode}</p>
                        )}
                      </div>
                      <span className={`text-[10px] font-black px-2 py-1 rounded ${
                        isLab ? 'bg-purple-100 text-purple-600' :
                        isLanguage ? 'bg-green-100 text-green-600' :
                        isElective ? 'bg-indigo-100 text-indigo-600' :
                        isBreak ? 'bg-amber-100 text-amber-600' :
                        isCustom ? 'bg-orange-100 text-orange-600' :
                        'bg-gray-100 text-gray-500'
                      } uppercase`}>
                        {isLab ? 'Lab' : isLanguage ? 'Language' : isElective ? 'Elective' : isBreak ? 'Break' : isCustom ? 'Custom' : (entry.type || 'Lecture')}
                      </span>
                    </div>

                    <div className="flex items-center gap-3 mb-4 text-sm font-bold text-gray-700">
                      <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" strokeWidth={2.5} /></svg>
                      {formatTime(entry.timeSlot?.start)} - {formatTime(entry.timeSlot?.end)}
                    </div>

                    {!isBreak && !isCustom && (
                      <div className="space-y-2 border-t pt-3 mt-3">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <span className="text-gray-400">👨‍🏫</span>
                          <span className="font-medium truncate">
                            {isElective ? (entry.electiveSelections?.length > 0 ? 'Multiple Faculty' : entry.facultyName || 'No Faculty') : (entry.facultyName || 'No Faculty')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <span className="text-gray-400">📍</span>
                          <span className="font-medium">Room: {entry.room || '—'}</span>
                        </div>
                        {isLab && entry.batchName && (
                          <div className="flex items-center gap-2 text-sm text-purple-600">
                            <span className="text-gray-400">🧪</span>
                            <span className="font-medium">{entry.batchName}</span>
                          </div>
                        )}
                        {isElective && entry.electiveGroupLabel && (
                          <div className="text-[10px] text-purple-600 font-bold uppercase mt-2">✨ {entry.electiveGroupLabel}</div>
                        )}
                      </div>
                    )}
                    
                    {isBreak && entry.label && (
                      <div className="space-y-2 border-t pt-3 mt-3">
                        <div className="flex items-center gap-2 text-sm text-amber-600">
                          <span className="text-gray-400">☕</span>
                          <span className="font-medium">{entry.label}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 text-gray-500 font-bold uppercase text-[10px] tracking-widest">
                  <tr>
                    <th className="px-6 py-4">Time</th>
                    <th className="px-6 py-4">Subject & Code</th>
                    <th className="px-6 py-4">Faculty</th>
                    <th className="px-6 py-4">Room / Batch</th>
                    <th className="px-6 py-4">Type</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {displayData.entries.map((entry, idx) => {
                    const sessionType = entry.sessionType || getSessionType(entry);
                    const isElective = sessionType === 'elective' || sessionType === 'language';
                    const isLab = sessionType === 'lab';
                    const isBreak = sessionType === 'break';
                    
                    const displayTitle = entry.displayTitle ||
                      (isBreak ? (entry.label || entry.name || 'BREAK') :
                       isLab && entry.batchName ? `${entry.subjectName || entry.label || 'Lab'} (${entry.batchName})` :
                       isElective ? (entry.electiveGroupDisplay || getGroupDisplayName(entry.electiveGroupId)) :
                       entry.subjectName || entry.label || 'Session');
                    
                    return (
                      <tr key={idx} onClick={() => setSelectedEntry(entry)} className="hover:bg-blue-50 cursor-pointer transition-colors">
                        <td className="px-6 py-4 font-bold text-gray-700 whitespace-nowrap">{formatTime(entry.timeSlot?.start)} - {formatTime(entry.timeSlot?.end)}</td>
                        <td className="px-6 py-4">
                          <div className="font-bold text-gray-900">
                            {displayTitle}
                          </div>
                          {entry.subjectCode && !isElective && !isBreak && (
                            <div className="text-xs text-gray-400 font-medium">{entry.subjectCode}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 font-medium text-gray-600">
                          {isBreak ? '-' : (isElective ? (entry.electiveSelections?.length > 0 ? 'Multiple Faculty' : entry.facultyName || '-') : (entry.facultyName || '-'))}
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-bold text-gray-800">{isBreak ? '-' : (entry.room || '-')}</div>
                          {isLab && entry.batchName && (
                            <div className="text-[10px] text-purple-600 font-black">BATCH: {entry.batchName}</div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`text-[10px] font-black px-2 py-1 rounded ${
                            isLab ? 'bg-purple-100 text-purple-600' :
                            isElective ? 'bg-indigo-100 text-indigo-600' :
                            isBreak ? 'bg-amber-100 text-amber-600' :
                            'bg-gray-100 text-gray-500'
                          }`}>
                            {isLab ? 'Lab' : isElective ? 'Elective' : isBreak ? 'Break' : 'Regular'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* MODAL */}
      {selectedEntry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in duration-200">
            <div className="p-6 bg-gray-50 border-b flex justify-between items-center">
              <h3 className="font-black text-gray-400 uppercase text-xs tracking-widest">Session Intelligence</h3>
              <button onClick={() => setSelectedEntry(null)} className="text-gray-400 hover:text-red-500 font-bold text-xl">✕</button>
            </div>
            
            <div className="p-8">
              <div className="mb-6">
                <div className="text-[10px] font-black text-blue-600 uppercase mb-1">Time Slot</div>
                <div className="text-2xl font-black text-gray-900">{formatTime(selectedEntry.timeSlot?.start)} - {formatTime(selectedEntry.timeSlot?.end)}</div>
              </div>

              {(() => {
                const sessionType = selectedEntry.sessionType || getSessionType(selectedEntry);
                if (sessionType === 'lab') {
                  return (
                    <div className="p-6 bg-purple-600 rounded-2xl text-white shadow-xl shadow-purple-100 mb-6">
                      <div className="text-[10px] font-black uppercase opacity-70 mb-1">Lab Session</div>
                      <div className="text-xl font-bold mb-1">{selectedEntry.subjectName || 'Lab'}</div>
                      {selectedEntry.subjectCode && <div className="text-sm opacity-80 font-medium">{selectedEntry.subjectCode}</div>}
                      <div className="mt-4 flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-lg">🧪</div>
                        <div className="font-bold text-sm">{selectedEntry.batchName || 'Not Specified'}</div>
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-lg">👨‍🏫</div>
                        <div className="font-bold text-sm">{selectedEntry.facultyName || 'No Faculty Assigned'}</div>
                      </div>
                    </div>
                  );
                }
                
                if (sessionType === 'language' || sessionType === 'elective') {
                  return (
                    <div className="mb-6">
                      <div className="p-6 bg-indigo-600 rounded-2xl text-white shadow-xl shadow-indigo-100 mb-4">
                        <div className="text-[10px] font-black uppercase opacity-70 mb-1">{sessionType === 'language' ? 'Language Session' : 'Elective Session'}</div>
                        <div className="text-xl font-bold mb-1">{selectedEntry.electiveGroupDisplay || getGroupDisplayName(selectedEntry.electiveGroupId)}</div>
                        {selectedEntry.electiveGroupLabel && <div className="text-sm opacity-80 font-medium">Label: {selectedEntry.electiveGroupLabel}</div>}
                      </div>
                      <div className="space-y-3 max-h-60 overflow-y-auto">
                        {(selectedEntry.electiveSelections || []).map((sel, i) => (
                          <div key={i} className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                            <div className="font-bold text-purple-800 text-sm">{sel.subjectName || getSubjectDisplayName(sel.subjectId)}</div>
                            {sel.subgroupSelections?.length > 0 ? (
                              <div className="mt-2 space-y-1">
                                {sel.subgroupSelections.map((sg, sgIdx) => (
                                  <div key={sg.subGroupId} className="text-xs text-gray-600">
                                    Group {sgIdx + 1}: {sg.facultyName || 'Not Assigned'}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-xs text-gray-600 mt-1">Faculty: {sel.facultyName || 'Not Assigned'}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                
                if (sessionType === 'break') {
                  return (
                    <div className="p-6 bg-amber-600 rounded-2xl text-white shadow-xl shadow-amber-100 mb-6">
                      <div className="text-[10px] font-black uppercase opacity-70 mb-1">Break Session</div>
                      <div className="text-xl font-bold mb-1">{selectedEntry.label || selectedEntry.name || 'BREAK'}</div>
                      {selectedEntry.description && <div className="text-sm opacity-80 mt-2">{selectedEntry.description}</div>}
                    </div>
                  );
                }
                
                if (sessionType === 'custom') {
                  return (
                    <div className="p-6 bg-orange-600 rounded-2xl text-white shadow-xl shadow-orange-100 mb-6">
                      <div className="text-[10px] font-black uppercase opacity-70 mb-1">Custom Session</div>
                      <div className="text-xl font-bold mb-1">{selectedEntry.label || 'CUSTOM SESSION'}</div>
                    </div>
                  );
                }
                
                return (
                  <div className="p-6 bg-blue-600 rounded-2xl text-white shadow-xl shadow-blue-100 mb-6">
                    <div className="text-[10px] font-black uppercase opacity-70 mb-1">Subject Details</div>
                    <div className="text-xl font-bold mb-1">{selectedEntry.subjectName || selectedEntry.label}</div>
                    {selectedEntry.subjectCode && <div className="text-sm opacity-80 font-medium">{selectedEntry.subjectCode}</div>}
                    <div className="mt-4 flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-lg">👨‍🏫</div>
                      <div className="font-bold text-sm">{selectedEntry.facultyName || 'No Faculty Assigned'}</div>
                    </div>
                  </div>
                );
              })()}

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <div className="text-[10px] font-black text-gray-400 uppercase mb-1">Location</div>
                  <div className="font-bold text-gray-800">{selectedEntry.room || '—'}</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <div className="text-[10px] font-black text-gray-400 uppercase mb-1">Day</div>
                  <div className="font-bold text-gray-800">{selectedDate.format('dddd, MMM DD')}</div>
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-gray-50">
              <button onClick={() => setSelectedEntry(null)} className="w-full py-4 bg-gray-900 text-white rounded-2xl font-bold shadow-lg hover:bg-black transition-all active:scale-95">Close Details</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}